import { useEffect, useRef, useState } from "react";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export const OrderPhotosDialog = ({ order, onOpenChange, onChanged }) => {
  const { t } = useApp();
  const [urls, setUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const photos = order?.photos || [];

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const map = {};
      for (const p of order?.photos || []) {
        try {
          const r = await api.get(`/work-orders/${order.id}/photos/${p.id}`, { responseType: "blob" });
          map[p.id] = URL.createObjectURL(r.data);
        } catch {}
      }
      if (!cancelled) setUrls(map);
    };
    if (order) load();
    return () => { cancelled = true; };
  }, [order]);

  const upload = (label) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/work-orders/${order.id}/photos?label=${label}`, fd);
      toast.success(t("saved"));
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (pid) => {
    try {
      await api.delete(`/work-orders/${order.id}/photos/${pid}`);
      toast.success(t("deleted"));
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };

  const Section = ({ label, fileRef }) => {
    const list = photos.filter((p) => p.label === label);
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{t(label)}</span>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid={`photo-input-${label}`} onChange={upload(label)} />
          <Button data-testid={`upload-photo-${label}`} type="button" size="sm" variant="outline" disabled={uploading}
            className="h-9" onClick={() => fileRef.current?.click()}>
            <Camera size={14} className="mr-1.5" /> {uploading ? "..." : t("uploadPhoto")}
          </Button>
        </div>
        {list.length === 0 && <div className="text-sm text-slate-300 border border-dashed border-slate-200 rounded-lg p-6 text-center">{t("noData")}</div>}
        <div className="grid grid-cols-3 gap-2">
          {list.map((p) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-50">
              {urls[p.id] ? (
                <img src={urls[p.id]} alt={label} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full animate-pulse bg-slate-100" />
              )}
              <button data-testid={`delete-photo-${p.id}`} onClick={() => remove(p.id)}
                className="absolute top-1.5 right-1.5 bg-white/90 rounded-md p-1.5 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{t("photos")} — {order?.order_number}</DialogTitle></DialogHeader>
        <div className="space-y-6">
          <Section label="before" fileRef={beforeRef} />
          <Section label="after" fileRef={afterRef} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
